<template>
  <div>
    <div v-if="countrySlug" class="bg-white">
      <div
        class="relative bg-grey-lightest w-full is-fixed-ar is-16x9 md:not-fixed-ar md:min-h-580 mobile-min-height-300"
      >
        <div class="ar-inside flex items-center justify-center px-5 py-10 md:py-15">
          <div class="bg-black opacity-40 absolute w-full h-full top-0 left-0 z-20"></div>
          <picture v-if="countryImage">
            <source
              :srcset="
                `${countryImage.urls.hero_mobile_wide} 1x, ${countryImage.urls.hero_mobile_wide_2x} 2x`
              "
              media="(max-width: 1024px)"
            />
            <source
              :srcset="
                `${countryImage.urls.hero} 1024w, ${countryImage.urls.hero_lg} 1920w, ${countryImage.urls.hero_lg_2x} 3840w`
              "
              sizes="100vw"
              media="(min-width: 1024px)"
            />
            <img
              :src="countryImage.urls.hero"
              :alt="countryImage.alt_text"
              class="full-bg-cover z-10"
            />
          </picture>
          <div class="text-center max-w-content-sm z-20 mb-2.5 md:m-5 md:pb-15">
            <h1
              v-if="countryName"
              class="font-primary font-bold text-white text-48 md:text-96 tracking-0.92 md:tracking-1 leading-none md:leading-0.92"
            >
              {{ countryName }}
            </h1>
            <h2
              v-if="countrySubheading"
              class="font-primary mb-5 font-bold text-white text-24 md:text-48 md:tracking-1 leading-none md:leading-0.92 mt-5"
            >
              {{ countrySubheading }}
            </h2>

            <div class="trustpilot-widget" style="margin-top:10px;" data-locale="en-GB" data-template-id="5419b732fbfb950b10de65e5" data-businessunit-id="633d92f693fe1ffa5a72ea87" data-style-height="24px" data-style-width="100%" data-theme="dark">
              <a href="https://uk.trustpilot.com/review/yellowwoodadventures.com" target="_blank" rel="noopener">Trustpilot</a>
            </div>
          </div>
        </div>
      </div>
      <div class="relative w-full md:-mt-15 z-30">
        <div class="bg-white xl:max-w-tabs md:mx-8 xl:mx-auto md:rounded-t-lg py-10 px-5 lg:px-10">
          <div
            class="max-w-content-sm mx-auto text-center font-sans text-blue-dark text-18 md:text-20 leading-1.56 md:leading-1.6"
          >
            <div v-if="countryDescription" v-html="countryDescription"></div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <slot></slot>
    </div>
    <div class="pb-7.5 lg:pb-15">
      <adventures-filter
        :default-country="countrySlug"
        @search-results="onSearchResults"
        @country="onSelectCountry"
      ></adventures-filter>

      <tab-container default-tab="adventures">
        <div
          class="w-full bg-grey-lightest"
          slot-scope="{ setTab, isTabActive, toggleMobileTabsMenu, currentTab }"
        >
          <div class="sticky z-20 top-12.5 lg:top-15 w-full bg-grey-lightest">
            <div v-if="countrySlug" class="block lg:hidden">
              <div
                class="expand-button relative w-full flex items-center justify-center h-12.5 bg-white shadow-top"
                @click="toggleMobileTabsMenu()"
              >
                <p
                  class="uppercase font-primary font-extrabold text-14 text-blue-brand tracking-0.22"
                >
                  {{ currentTab }}
                </p>
                <div class="absolute top-0 right-0 h-12.5 w-12.5 flex items-center justify-center">
                  <span class="triangle triangle-md expand-triangle"></span>
                </div>
              </div>
              <ul
                class="expand-list w-full uppercase font-primary font-extrabold text-14 text-blue-brand tracking-0.22"
              >
                <li
                  class="w-full h-12.5 items-center justify-center bg-white"
                  :class="isTabActive('adventures') ? 'hidden' : 'flex'"
                  @click="[setTab('adventures'), toggleMobileTabsMenu()]"
                >
                  Adventures
                </li>
                <li
                  class="w-full h-12.5 items-center justify-center bg-white"
                  :class="isTabActive('overview') ? 'hidden' : 'flex'"
                  @click="[setTab('overview'), toggleMobileTabsMenu()]"
                >
                  Overview
                </li>
                <li
                  class="w-full h-12.5 items-center justify-center bg-white"
                  :class="isTabActive('articles') ? 'hidden' : 'flex'"
                  @click="[setTab('articles'), toggleMobileTabsMenu()]"
                >
                  Articles
                </li>
              </ul>
            </div>

            <ul v-if="countrySlug" class="max-w-tabs mx-auto flex h-12.5 lg:h-18 px-2.5 lg:px-0">
              <li
                class="h-full flex flex-tabs items-center justify-center rounded-b-lg hover:bg-white-0.5 font-primary font-extrabold text-blue-brand text-16 uppercase tracking-1/4 leading-1 text-center cursor-pointer"
                :class="{ 'bg-white hover:bg-white': isTabActive('adventures') }"
                @click.prevent="setTab('adventures')"
              >
                <span class="hidden lg:block">Adventures</span>
                <span
                  class="block lg:hidden h-2.5 w-2.5 rounded-full"
                  :class="isTabActive('adventures') ? 'bg-blue-dark' : 'bg-grey-lighter'"
                ></span>
              </li>
              <li
                class="h-full flex flex-tabs items-center justify-center rounded-b-lg hover:bg-white-0.5 font-primary font-extrabold text-blue-brand text-16 uppercase tracking-1/4 leading-1 text-center cursor-pointer"
                :class="{ 'bg-white hover:bg-white': isTabActive('overview') }"
                @click.prevent="setTab('overview')"
              >
                <span class="hidden lg:block">Overview</span>
                <span
                  class="block lg:hidden h-2.5 w-2.5 rounded-full"
                  :class="isTabActive('overview') ? 'bg-blue-dark' : 'bg-grey-lighter'"
                ></span>
              </li>
              <li
                class="h-full flex flex-tabs items-center justify-center rounded-b-lg hover:bg-white-0.5 font-primary font-extrabold text-blue-brand text-16 uppercase tracking-1/4 leading-1 text-center cursor-pointer"
                :class="{ 'bg-white hover:bg-white': isTabActive('articles') }"
                @click.prevent="setTab('articles')"
              >
                <span class="hidden lg:block">Articles</span>
                <span
                  class="block lg:hidden h-2.5 w-2.5 rounded-full"
                  :class="isTabActive('articles') ? 'bg-blue-dark' : 'bg-grey-lighter'"
                ></span>
              </li>
            </ul>
          </div>

          <div v-if="countrySlug === null || isTabActive('adventures')" id="adventures">
            <div class="w-full max-w-content-xl mx-auto">
              <bread-crumbs
                :items="[
                  {
                    url: '/',
                    label: 'Home',
                  },
                  {
                    url: '#adventures',
                    label: 'Adventures',
                  },
                  {
                    url: route('countries.show', { country: countrySlug }),
                    label: countryName,
                  },
                ]"
                class="my-5 px-5"
              ></bread-crumbs>
            </div>

            <adventures-list
              :adventures="adventures"
              :total="total"
              :order="order"
              :order-by="orderBy"
              @update-order-by="onUpdateOrderBy"
              @update-order="onUpdateOrder"
            ></adventures-list>

            <p v-if="loading" class="py-5 text-center text-18 md:text-20">Finding adventures...</p>
            <button
              v-else-if="showLoadMore"
              class="button block my-5 mx-auto min-w-280 md:min-w-360"
              @click.prevent="loadNextPage"
            >
              View more adventures
            </button>
            <div
              v-if="error"
              class="py-5 md:py-15 text-center mx-auto max-w-screen-sm lg:max-w-content-sm"
            >
              <p class="text-18 md:text-20 mb-5">{{ error }}</p>
              <button class="button block mx-auto min-w-280 md:min-w-360" @click.prevent="tryAgain">
                Try again
              </button>
            </div>
          </div>

          <div v-if="countrySlug && isTabActive('overview')" id="overview">
            <div class="w-full max-w-content-xl mx-auto">
              <bread-crumbs
                :items="[
                  {
                    url: '/',
                    label: 'Home',
                  },
                  {
                    url: route('countries.show', { country: countrySlug }),
                    label: countryName,
                  },
                  {
                    url: '#overview',
                    label: 'Overview',
                  },
                ]"
                class="my-5 px-5"
              ></bread-crumbs>
            </div>

            <div class="max-w-screen-sm md:flex mx-auto my-4 font-primary">
              <div v-if="countryOverviewNavItems.length > 0" class="md:w-1/2 p-4">
                <h3 class="text-26 mb-2 font-extrabold text-blue-brand">Contents</h3>
                <ul class="contents-menu mb-5 ml-1.5">
                  <li class="mb-1" v-for="navItem in countryOverviewNavItems" :key="navItem.row_id">
                    <a :href="`#${navItem.row_id}`" class="flex items-center">
                      <span class="h-1.5 w-1.5 bg-yellow-brand rounded-full mr-2.5"></span>
                      <span class="underline text-16 leading-1.63">{{ navItem.row_label }}</span>
                    </a>
                  </li>
                </ul>
              </div>
              <div class="md:w-1/2 p-4">
                <div class="bg-white rounded shadow flex justify-center p-4">
                  <div class="mr-4">
                    <h3 class="uppercase text-center font-bold mb-2">
                      Currency
                    </h3>
                    <div
                      class="rounded-full bg-grey-lightest w-30 h-30 flex items-center justify-center text-blue-dark text-32 font-extrabold"
                    >
                      {{ countryCurrencySymbol }}
                    </div>
                    <div class="text-center text-blue-dark font-extrabold text-14 mt-2">
                      {{ countryCurrencyName }}
                    </div>
                  </div>
                  <div>
                    <h3 class="uppercase text-center font-bold mb-2">
                      Time
                    </h3>
                    <div
                      class="pie p-3"
                      :class="countryGmtOffset > 0 ? 'is-positive' : 'is-negative'"
                      :style="{ '--pie-value': countryGmtOffsetAbsolute }"
                    >
                      <div
                        class="rounded-full bg-white shadow w-full h-full flex items-center justify-center text-blue-dark text-32 font-extrabold"
                      >
                        {{ countryGmtOffset >= 0 ? '+' : '' }}{{ countryGmtOffset }}
                      </div>
                    </div>
                    <div class="text-center text-blue-dark font-extrabold text-14 mt-2">
                      GMT {{ countryGmtOffset >= 0 ? '+' : '' }}{{ countryGmtOffset }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-html="countryOverview"></div>
          </div>

          <div v-if="countrySlug && isTabActive('articles')" id="articles">
            <div class="w-full max-w-content-xl mx-auto">
              <bread-crumbs
                :items="[
                  {
                    url: '/',
                    label: 'Home',
                  },
                  {
                    url: route('countries.show', { country: countrySlug }),
                    label: countryName,
                  },
                  {
                    url: '#articles',
                    label: 'Articles',
                  },
                ]"
                class="my-5 px-5"
              ></bread-crumbs>
            </div>

            <posts-index
              :country="countrySlug"
              no-posts-text="Looks like we don't currently have any posts for this country."
              all-posts-btn-text="View all posts"
              all-posts-url="/posts"
            ></posts-index>
          </div>
        </div>
      </tab-container>
    </div>
  </div>
</template>

<script>
import Route from '../src/Route';
import AdventuresFilter from './AdventuresFilter.vue';
import AdventuresList from './AdventuresList.vue';
import BreadCrumbs from './BreadCrumbs.vue';
import get from 'lodash/get';
import TabContainer from './TabContainer';
import PostsIndex from './PostsIndex.vue';

export default {
  components: { AdventuresFilter, AdventuresList, BreadCrumbs, TabContainer, PostsIndex },

  props: {
    country: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      total: 0,
      adventures: [],
      searchParams: {},
      perPage: 9,
      currentPage: 1,
      loading: false,
      error: null,
      currentTab: 'adventures',
      loadingCountry: false,
      countrySlug: this.country,
      countryError: null,
      countryCurrencyName: null,
      countryCurrencySymbol: null,
      countryGmtOffset: null,
      countryImage: null,
      countryName: null,
      countrySubheading: null,
      countryOverview: null,
      countryOverviewNavItems: [],
      countryDescription: null,
      order: 'ASC',
      orderBy: 'date',
    };
  },

  computed: {
    showLoadMore() {
      return this.total !== this.adventures.length && !this.error;
    },
    countryGmtOffsetAbsolute() {
      return Math.abs(this.countryGmtOffset);
    },
  },

  created() {
    if (this.countrySlug) {
      this.getCountry().then(() => {
        this.getAdventures();
      });
    } else {
      this.getAdventures();
    }
  },

  methods: {
    getAdventures() {
      this.loading = true;
      this.error = null;

      axios
        .get(Route('json.adventures.index'), {
          params: Object.assign(
            {
              per_page: this.perPage,
              page: this.currentPage,
              country: this.countrySlug,
              order: this.order,
              order_by: this.orderBy,
            },
            this.searchParams
          ),
        })
        .then((response) => {
          if (response.status === 200) {
            this.total = response.data.total;
            this.adventures = [...this.adventures, ...response.data.adventures];
          } else {
            throw 'There was a problem.';
          }
        })
        .catch((error) => {
          this.error = 'Sorry there was a problem loading adventures.';
        })
        .then(() => {
          this.loading = false;
        });
    },

    getCountry() {
      this.loadingCountry = true;
      this.countryError = null;
      this.countryCurrencyName = null;
      this.countryCurrencySymbol = null;
      this.countryGmtOffset = null;
      this.countryImage = null;
      this.countryName = null;
      this.countryOverview = null;
      this.countryDescription = null;
      this.countryOverviewNavItems = [];
      this.countrySubheading = null;

      if (!this.countrySlug) {
        return;
      }

      return axios
        .get(
          Route('json.countries.show', {
            country: this.countrySlug,
          })
        )
        .then((response) => {
          if (response.status === 200) {
            this.countryCurrencyName = get(response, 'data.country.currency_name');
            this.countryCurrencySymbol = get(response, 'data.country.currency_symbol');
            this.countryGmtOffset = get(response, 'data.country.gmt_offset');
            this.countryImage = get(response, 'data.country.image');
            this.countryName = get(response, 'data.country.name');
            this.countryOverview = get(response, 'data.country.overview');
            this.countryOverviewNavItems = get(response, 'data.country.overview_nav_items', []);
            this.countryDescription = get(response, 'data.country.description');
            this.countrySubheading = get(response, 'data.country.subheading');
          } else {
            throw 'There was a problem.';
          }
        })
        .catch((e) => {
          console.error(e);
          this.error = 'There was a problem getting information about this country';
        })
        .then(() => {
          this.loadingCountry = false;
        });
    },

    onSearchResults(results) {
      this.searchParams = Object.assign(this.searchParams, results);
      this.adventures = [];
      this.currentPage = 1;
      this.getAdventures();
    },

    onSelectCountry(countrySlug) {
      this.countrySlug = countrySlug;
      this.getCountry();
    },

    loadNextPage() {
      ++this.currentPage;
      this.getAdventures();
    },

    tryAgain() {
      this.getAdventures();
    },

    onUpdateOrderBy(orderBy) {
      this.orderBy = orderBy;
      this.adventures = [];
      this.currentPage = 1;
      this.getAdventures();
    },

    onUpdateOrder(order) {
      this.order = order;
      this.adventures = [];
      this.currentPage = 1;
      this.getAdventures();
    },

    route() {
      return Route(...arguments);
    },
  },

  watch: {
    countrySlug() {
      this.getCountry();
    },
  },
};
</script>
